import React from 'react';
import { formatDublinDate } from '../../utils/date';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowRightIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import ParentLeftChildBanner from '../../components/parent/ParentLeftChildBanner';
import { useGetMyInvitationsQuery } from '../../store/api/localApi';

/**
 * /parent/:studentId/forms — full forms inbox for one child.
 *
 * The kid is the frame; rows don't repeat the kid name. Items split into
 * "Pending" (action required) and "Completed" (audit trail). Tapping a
 * pending row goes to the form-fill page under the per-kid namespace.
 */
const ParentChildForms: React.FC = () => {
  const { activeStudent, isLoading, parentHomePath } = useActiveStudent();
  const { data, isLoading: invitationsLoading } = useGetMyInvitationsQuery();

  if (isLoading || invitationsLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (!activeStudent) {
    return <Navigate to={parentHomePath ?? '/parent'} replace />;
  }

  const all = data?.invitations ?? [];
  const childInvitations = all.filter((inv) => inv.studentId === activeStudent.id);
  const pending = childInvitations.filter((inv) => inv.status === 'PENDING');
  const completed = childInvitations.filter((inv) => inv.status !== 'PENDING');
  // Task 17: form submission is a write. A LEFT child's completed forms stay
  // readable, but the Pending (fill) section is hidden and the banner explains.
  const hasLeft = activeStudent.status === 'LEFT';

  return (
    <div className="py-6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        <div>
          <Link
            to={`/parent/${activeStudent.id}`}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to {activeStudent.firstName || 'profile'}
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-gray-900">Forms</h1>
          <p className="text-sm text-gray-500">For {activeStudent.displayName}</p>
        </div>

        {hasLeft && (
          <ParentLeftChildBanner
            childName={activeStudent.firstName || activeStudent.displayName}
            locationName={activeStudent.locationName}
          />
        )}

        {/* Pending — hidden for a LEFT child (submitting is a write). */}
        {!hasLeft && (
          <section data-testid="parent-forms-pending" className="bg-white rounded-lg shadow">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">
                Pending
                {pending.length > 0 && (
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    · {pending.length} form{pending.length === 1 ? '' : 's'}
                  </span>
                )}
              </h2>
            </div>
            {pending.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                <CheckCircleIcon
                  aria-hidden="true"
                  className="h-6 w-6 mx-auto mb-2 text-green-500"
                />
                All caught up.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {pending.map((inv) => (
                  <li key={inv.id}>
                    <Link
                      to={`/parent/${activeStudent.id}/forms/${inv.id}/fill`}
                      data-testid={`parent-forms-pending-${inv.id}`}
                      className="block px-4 py-3 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <DocumentTextIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {inv.formTitle}
                          </div>
                          <div className="text-xs text-gray-500">
                            {inv.deadline
                              ? `Due ${formatDublinDate(inv.deadline)}`
                              : 'No deadline'}
                          </div>
                        </div>
                        <span className="text-xs font-medium text-primary-700 hidden sm:inline">
                          Fill out
                        </span>
                        <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Completed — only shown when there's something to show */}
        {completed.length > 0 && (
          <section data-testid="parent-forms-completed" className="bg-white rounded-lg shadow">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">Completed</h2>
            </div>
            <ul className="divide-y divide-gray-100">
              {completed.map((inv) => (
                <li key={inv.id} className="px-4 py-3 flex items-center gap-3 text-gray-600">
                  <CheckCircleIcon
                    aria-hidden="true"
                    className="h-5 w-5 text-green-500 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{inv.formTitle}</div>
                    <div className="text-xs text-gray-500">Status: {inv.status}</div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

export default ParentChildForms;
